/* eslint-disable @typescript-eslint/no-unused-vars */
import { PolicyTypeEnum } from '@Policy/utils/constants';
import i18nInst from 'src/i18n';
import {
  addEmails,
  addItems,
  checkActionByType,
  checkEmailFormat,
  checkFileBlockingExtension,
  checkNotification,
  checkReplacementNameOrigin,
  checkReplacementTextOrigin,
  checkToken,
  removeItem,
  showAddApprovedSenderExceedListLimitNotification,
  showAddApprovedSenderListErrNotification,
  showAddEmailAddressExceedListLimitNotification,
} from 'src/pages/Policy/utils/fns';
import {
  ActionData,
  ActionEnum,
  ApplyBoundEnum,
  EnableDisableEnum,
  ExchangeOnlinePolicyItem,
  Notification,
  PolicyFilters,
  VirusScanFilter,
  VsActionKeys,
  VsNotifyTypeKeys,
  VsSuspiciousTagKeys,
} from 'src/pages/Policy/utils/types';
import { vsActionDataSelector } from './selectors';

export const createVirusScanSlice = (
  setPolicy: any,
  getPolicy: any,
  getAll: any
) => {
  const { t } = i18nInst;
  return {
    setVsState: (payload: Partial<VirusScanFilter>) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmVirusscans: {
          ...state.tmVirusscans,
          ...payload,
        },
      }));
    },
    setVsNtfState: (payload: Partial<Notification>) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmVirusscans: {
          ...state.tmVirusscans,
          notification: {
            ...state.tmVirusscans.notification,
            ...payload,
          },
        },
      }));
    },
    setVsEnablePredictiveMachineLearning: (
      vsEnablePredictiveMachineLearning: EnableDisableEnum
    ) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => {
        state.tmVirusscans.vsEnablePredictiveMachineLearning =
          vsEnablePredictiveMachineLearning;
        state.tmVirusscans.vsEnablePredictiveMachineLearningFeedback =
          vsEnablePredictiveMachineLearning;
      });
    },
    setVsSpecifiedFileTypeObjects: (selectedKeys: string[]) => {
      const fileTypes = getAll().config.fileTypes;
      const typeClasses = fileTypes[0].children.map((typeClass: any) => {
        return typeClass.key;
      });
      const newSelectedKeys = selectedKeys.filter(
        (key) => !typeClasses.includes(key)
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmVirusscans: {
          ...state.tmVirusscans,
          vsSpecifiedFileTypeObjects: newSelectedKeys.join('|'),
        },
      }));
    },
    vsActionHandler: (key: VsActionKeys) => (action: ActionEnum) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmVirusscans: {
          ...state.tmVirusscans,
          [key]: action,
        },
      }));
    },
    vsNotifyTypeHandler: (key: VsNotifyTypeKeys) => {
      const currentState = getPolicy();
      const originValue = currentState.tmVirusscans[key];
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmVirusscans: {
          ...state.tmVirusscans,
          [key]: originValue ? 0 : 1,
        },
      }));
    },
    setVsUnscannableFileOptions: (props: {
      replaceFileName: string;
      replaceText: string;
      actionConfig: Partial<ActionData>[];
    }) => {
      const { replaceFileName, replaceText, actionConfig } = props;
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmVirusscans: {
          ...state.tmVirusscans,
          vsReplacementFileNameUnscannable: replaceFileName,
          vsReplacementTextUnscannable: replaceText,
          vsEncryptedCompressedFileActionType: actionConfig[0].action,
          vsEncryptedCompressedFileNotifyType: actionConfig[0].notifyType,
          vsOtherEncryptedFileActionType: actionConfig[1].action,
          vsOtherEncryptedFileNotifyType: actionConfig[1].notifyType,
          vsTextTagSubjectContent: actionConfig[1].suspiciousTag,
        },
      }));
    },
    addSpecifiedFileExt: (ext: string) => {
      const currentState = getPolicy();
      const {
        tmVirusscans: { vsSpecifiedFileExtObjects },
      } = currentState;
      const currentStore = getAll();
      const listLimit =
        currentStore.config.limitation.approvedFileExtensionCount;
      const { result, errList } = addItems(
        ext,
        vsSpecifiedFileExtObjects,
        listLimit,
        -1,
        checkFileBlockingExtension
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmVirusscans: {
          ...state.tmVirusscans,
          vsSpecifiedFileExtObjects: result,
        },
      }));
      // process errorList
      // errorList will be shown on ui, so need to do char2entity
      console.log('errorList', errList);
    },
    removeSpecifiedFileExt: (value: string) => {
      const currentState = getPolicy();
      const {
        tmVirusscans: { vsSpecifiedFileExtObjects },
      } = currentState;
      const outSeparator = '|';
      const successList = vsSpecifiedFileExtObjects.split(outSeparator).sort();
      if (successList.includes(value)) {
        successList.splice(successList.indexOf(value), 1);
      }
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmVirusscans: {
          ...state.tmVirusscans,
          vsSpecifiedFileExtObjects: successList.join(outSeparator),
        },
      }));
    },
    addVsExceptSenders: (keys: string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const separator = ';';
      const {
        config: { limitation },
      } = currentStore;
      const vsDisarmExceptSenderString =
        currentState.tmVirusscans.vsDisarmExceptSenderString;
      const includeDomain = false;

      const { exceedListLimit, errList, entriesStr } = addEmails(
        keys,
        vsDisarmExceptSenderString,
        includeDomain,
        limitation.singleApprovedListMaxCount,
        separator,
        limitation.vsDisarmExceptListMaxCount
      );

      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmVirusscans: {
          ...state.tmVirusscans,
          vsDisarmExceptSenderString: entriesStr,
        },
      }));
      // process exceedLimit
      if (exceedListLimit) {
        showAddEmailAddressExceedListLimitNotification(
          limitation.approvedListMaxCount
        );
        return;
      }
      // process errorList
      if (errList.length) {
        console.error('addVsExceptSender error list:', errList);
        showAddApprovedSenderListErrNotification(errList);
      }
    },
    removeVsExceptSenders: (key: string) => {
      const currentState = getPolicy();
      const originExceptSenders =
        currentState.tmVirusscans.vsDisarmExceptSenderString;
      const separator = ';';
      const result = removeItem(key, originExceptSenders, separator);
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmVirusscans: {
          ...state.tmVirusscans,
          vsDisarmExceptSenderString: result,
        },
      }));
    },
    addVsExceptRecipients: (keys: string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const separator = ';';
      const vsDisarmExceptReceiverString =
        currentState.tmVirusscans.vsDisarmExceptReceiverString;
      const {
        config: { limitation },
      } = currentStore;
      const includeDomain = false;

      const { exceedListLimit, errList, entriesStr } = addEmails(
        keys,
        vsDisarmExceptReceiverString,
        includeDomain,
        limitation.singleApprovedListMaxCount,
        separator,
        limitation.vsDisarmExceptListMaxCount
      );

      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmVirusscans: {
          ...state.tmVirusscans,
          vsDisarmExceptReceiverString: entriesStr,
        },
      }));
      // process exceedLimit
      if (exceedListLimit) {
        showAddEmailAddressExceedListLimitNotification(
          limitation.approvedListMaxCount
        );
        return;
      }
      // process errorList
      if (errList.length) {
        console.error('addVsExceptRecipients error list:', errList);
        showAddApprovedSenderListErrNotification(errList);
      }
    },
    removeVsExceptRecipient: (key: string) => {
      const currentState = getPolicy();
      const separator = ';';
      const originExceptRecipients =
        currentState.tmVirusscans.vsDisarmExceptReceiverString;
      const result = removeItem(key, originExceptRecipients, separator);
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmVirusscans: {
          ...state.tmVirusscans,
          vsDisarmExceptReceiverString: result,
        },
      }));
    },
    importVsExceptSenders: (entries: string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const separator = ';';
      const vsDisarmExceptSenderString =
        currentState.tmVirusscans.vsDisarmExceptSenderString;
      const listLimit =
        currentStore.config.limitation.vsDisarmExceptListMaxCount;
      const validEntries = entries.filter((item: string) => item !== '');
      const existedEntries = vsDisarmExceptSenderString
        .split(separator)
        .filter((entry: string) => entry !== '');
      if (validEntries.length + existedEntries.length > listLimit) {
        showAddApprovedSenderExceedListLimitNotification(listLimit);
        return;
      }
      currentStore.addVsExceptSenders(validEntries);
    },
    importVsExceptRecipients: (entries: string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const separator = ';';
      const vsDisarmExceptReceiverString =
        currentState.tmVirusscans.vsDisarmExceptReceiverString;
      const listLimit =
        currentStore.config.limitation.vsDisarmExceptListMaxCount;
      const validEntries = entries.filter((item: string) => item !== '');
      const existedEntries = vsDisarmExceptReceiverString
        .split(separator)
        .filter((entry: string) => entry !== '');
      if (validEntries.length + existedEntries.length > listLimit) {
        showAddApprovedSenderExceedListLimitNotification(listLimit);
        return;
      }
      currentStore.addVsExceptRecipients(validEntries);
    },
    validateVirusScan: () => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const filter = currentState.tmVirusscans;
      const actionData = vsActionDataSelector(currentStore);
      currentStore.replaceInvalidAction(actionData);

      const result = {
        [PolicyFilters.tmVirusscans]: {},
      };
      const { policyType } = filter;
      const {
        notifyMessageMaxLength,
        notifySubjectMaxLength,
        fileReplacement,
      } = currentStore.config.limitation;

      // check action
      const actionKey = 'vsOtherEncryptedFileActionType';
      const suspiciousTagKey = VsSuspiciousTagKeys.vsTextTagSubjectContent;
      const actionType = filter[actionKey];
      const suspiciousTagValue = filter[suspiciousTagKey];
      let checkResult: any = checkActionByType(suspiciousTagValue, actionType);
      if (checkResult) {
        // return directly when meet an error
        result[PolicyFilters.tmVirusscans] = {
          [suspiciousTagKey]: checkResult,
        };
        return result;
      }
      // check notification
      checkResult = checkNotification(
        filter.notification,
        notifyMessageMaxLength,
        notifySubjectMaxLength
      );
      if (checkResult) {
        // return directly when meet an error
        result[PolicyFilters.tmVirusscans] = checkResult;
        return result;
      }
      // check replacement name and text
      const replacementNameKeys = [
        'vsReplacementFileName',
        'vsReplacementFileNameUnscannable',
      ];
      for (const nameKey of replacementNameKeys) {
        checkResult = checkReplacementNameOrigin(filter[nameKey]);
        if (checkResult) {
          // return directly when meet an error
          result[PolicyFilters.tmVirusscans] = {
            [nameKey]: checkResult,
          };
          return result;
        }
      }
      const replacementTextKeys = [
        'vsReplacementText',
        'vsReplacementTextUnscannable',
      ];
      for (const textKey of replacementTextKeys) {
        checkResult = checkReplacementTextOrigin(
          filter[textKey],
          fileReplacement
        );
        if (checkResult) {
          // return directly when meet an error
          result[PolicyFilters.tmVirusscans] = {
            [textKey]: checkResult,
          };
          return result;
        }
      }
      // check token
      if (
        ![PolicyTypeEnum.Exchange, PolicyTypeEnum.Gmail].includes(policyType)
      ) {
        checkToken({
          keyValue: filter.vsReplacementText,
          keyName: 'vsReplacementText',
        });
        if (checkResult) {
          // return directly when meet an error
          result[PolicyFilters.tmVirusscans] = checkResult;
          return result;
        }
      }
      // check file extensions
      const scanFileExtensions: string = filter.vsSpecifiedFileExtObjects;
      if (
        filter.vsDefaultScanningType === 2 &&
        filter.vsEnableScanFileExt === EnableDisableEnum.enabled &&
        scanFileExtensions.trim() === ''
      ) {
        result[PolicyFilters.tmVirusscans] = {
          vsSpecifiedFileExtObjects: {
            title: t(`policy.error.noExtSpecifiedTitle`),
            content: t(`policy.error.noExtSpecified`),
          },
        };
        return result;
      }
      return;
    },
    setVsApplyBound: (payload: { [key: string]: ApplyBoundEnum }) => {
      const currentStore = getAll();
      const policy = getPolicy();
      const { setTempVars } = currentStore;
      if (typeof payload.vsApplyBound !== 'undefined') {
        const vsApplyBound = payload.vsApplyBound;
        if (vsApplyBound === ApplyBoundEnum.incomming) {
          setTempVars({ ddaApplyBound: policy.tmDdafilters.ddaApplyBound });
          setPolicy((state: ExchangeOnlinePolicyItem) => {
            state.tmVirusscans.vsApplyBound = vsApplyBound;
            state.tmDdafilters.ddaApplyBound = vsApplyBound;
          });
        } else {
          setPolicy((state: ExchangeOnlinePolicyItem) => {
            const originDdaApplyBound = currentStore.tempVars.ddaApplyBound;
            state.tmVirusscans.vsApplyBound = vsApplyBound;
            state.tmDdafilters.ddaApplyBound = originDdaApplyBound
              ? originDdaApplyBound
              : state.tmDdafilters.ddaApplyBound;
          });
        }
      }
    },
  };
};
